import { IntrinsicTask } from '../../constants/customization';
import { PutCustomizationTaskBodyContract } from '../../data/contracts/customization';
import { AzureDevOpsBranch } from '../../models/azure-dev-ops';
import { PutCustomizationTask, UploadedFileContent } from '../../models/customization';
import { HibernateSupport } from '../../models/pool';
import { SerializableMap } from '../../types/serializable-map';
import { CapitalizedUnionMap, UnionMap, UnionValueMap } from '../../types/union-map';
import { ProjectViewModel } from '../common/models';

export interface CustomizationData {
    taskList?: PutCustomizationTask[];
    cloneUrl?: string;
    branch?: AzureDevOpsBranch;
    contents?: string[];
    repoFiles?: string[];
    files?: UploadedFileContent[];
}

export interface AddDevBoxFormData {
    devBoxName: string;
    selectedProject: AddDevBoxFormProjectViewModel | undefined;
    selectedPool: PoolViewModel | undefined;
    selectedImage: ImageViewModel | undefined;
    selectedRegion: RegionViewModel | undefined;
    selectedSize: SizeViewModel | undefined;
    applyCustomizationsEnabled: boolean;
    uploadFileEnabled: boolean;
    fileFromRepoEnabled: boolean;
    fileCustomizations: CustomizationData | undefined;
    repoCustomizations: CustomizationData | undefined;
    selectedIntrinsicTasks: IntrinsicTask[] | undefined;
}

export interface AddDevBoxFormErrorsByField {
    devBoxName?: string;
    selectedProject?: string;
    selectedPool?: string;
    repoUri?: string;
}

export interface AddDevBoxFormProjectViewModel extends ProjectViewModel {
    usedDevBoxes: number;
    recentlyUsed?: boolean;
}

export const CustomizationGroupName = 'Initial';

export type CustomizationFileSchemaVersion = 'V1';

export const CustomizationFileSchemaVersion: UnionValueMap<CustomizationFileSchemaVersion, string> = {
    V1: '1.0',
};

export type CustomizationPanels = 'None' | 'CustomizationFiles' | 'Summary';

export const CustomizationPanels: UnionMap<CustomizationPanels> = {
    None: 'None',
    CustomizationFiles: 'CustomizationFiles',
    Summary: 'Summary',
};

export type CustomizationTaskParameter = { [parameterName: string]: string };

export type CustomizationTaskContract = { task: string; inputs?: CustomizationTaskParameter };

export interface PoolViewModel {
    id: string;
    name: string;
    cpuCount: number;
    memoryInGb: number;
    diskSizeInGb: number;
    region: string;
    imageName?: string;
    imageVersion?: string;
    lastUpdated?: Date;
    osType: string;
    supportsHibernate: boolean;
    hibernateSupport: HibernateSupport;
    displayName: string;
}

export type PoolKey = Exclude<keyof PoolViewModel, 'id' | 'osType' | 'hibernateSupport' | 'displayName'>;

export const PoolKey: CapitalizedUnionMap<PoolKey> = {
    CpuCount: 'cpuCount',
    DiskSizeInGb: 'diskSizeInGb',
    ImageName: 'imageName',
    ImageVersion: 'imageVersion',
    LastUpdated: 'lastUpdated',
    MemoryInGb: 'memoryInGb',
    Name: 'name',
    Region: 'region',
    SupportsHibernate: 'supportsHibernate',
};

export interface ImageViewModel {
    id: string;
    name: string;
    regionDisplayName: string;
    region: string;
    imageName: string;
    poolName: string;
    version?: string;
    lastUpdated?: Date;
    recentlyUsed?: boolean;
}

export type LatencyBand = 'Poor' | 'Fair' | 'Good' | 'Excellent';

export const LatencyBand: UnionMap<LatencyBand> = {
    Poor: 'Poor',
    Fair: 'Fair',
    Good: 'Good',
    Excellent: 'Excellent',
};

export interface RegionViewModel {
    id: string;
    sourceRegion: string;
    name: string;
    region: string;
    latencyBand: LatencyBand;
    roundTripTime: number;
}

export interface RegionDropdownOption extends RegionViewModel {
    hidden?: boolean;
    disabled?: boolean;
}

export interface SizeViewModel {
    id: string;
    poolId: string;
    imageName: string;
    region: string;
    cpuCount: number;
    memoryInGb: number;
    diskSizeInGb: number;
    hibernateSupport: HibernateSupport;
    isDuplicateSize?: boolean;
}

export type ImageKey = Exclude<
    keyof ImageViewModel,
    'id' | 'region' | 'regionDisplayName' | 'recentlyUsed' | 'poolName'
>;

export const ImageKey: CapitalizedUnionMap<ImageKey> = {
    Name: 'name',
    ImageName: 'imageName',
    Version: 'version',
    LastUpdated: 'lastUpdated',
};

export type RegionKey = Exclude<keyof RegionViewModel, 'id' | 'region' | 'sourceRegion'>;

export const RegionKey: CapitalizedUnionMap<RegionKey> = {
    Name: 'name',
    LatencyBand: 'latencyBand',
    RoundTripTime: 'roundTripTime',
};

export type IntrinsicTaskKey = Extract<keyof PutCustomizationTaskBodyContract, 'name'>;

export const IntrinsicTaskKey: CapitalizedUnionMap<IntrinsicTaskKey> = {
    Name: 'name',
};

export type DevBoxDataPlaneIdMap = SerializableMap<string>;
export type ProjectToDevBoxDataPlaneIdMap = SerializableMap<DevBoxDataPlaneIdMap>;
export type ImageViewModelMap = SerializableMap<ImageViewModel[][]>;
export type RegionViewModelMap = SerializableMap<RegionViewModel>;
export type SizeViewModelMap = SerializableMap<SizeViewModel>;
export type ProjectToSizeViewModelMap = SerializableMap<SizeViewModel[]>;
export type ProjectToPoolViewModelMap = SerializableMap<PoolViewModel[]>;
export type PoolViewModelMap = SerializableMap<PoolViewModel>;

// TODO: add arguments to function a la serializable-map if they're ever needed
export const DevBoxDataPlaneIdMap = (): DevBoxDataPlaneIdMap => ({});
export const ProjectToPoolViewModelMap = (): ProjectToPoolViewModelMap => ({});
export const ImageViewModelMap = (): ImageViewModelMap => ({});
export const RegionViewModelMap = (): RegionViewModelMap => ({});
export const ProjectToDevBoxDataPlaneIdMap = (): ProjectToDevBoxDataPlaneIdMap => ({});
export const PoolViewModelMap = (): PoolViewModelMap => ({});

export type AddDevBoxFormField =
    | 'devBoxName'
    | 'selectedProject'
    | 'selectedPool'
    | 'selectedImage'
    | 'selectedRegion'
    | 'selectedSize'
    | 'applyCustomizationsEnabled'
    | 'uploadFileEnabled'
    | 'fileFromRepoEnabled'
    | 'fileCustomizations'
    | 'repoCustomizations'
    | 'selectedIntrinsicTasks';

export const AddDevBoxFormField: CapitalizedUnionMap<AddDevBoxFormField> = {
    DevBoxName: 'devBoxName',
    SelectedProject: 'selectedProject',
    SelectedPool: 'selectedPool',
    SelectedImage: 'selectedImage',
    SelectedRegion: 'selectedRegion',
    SelectedSize: 'selectedSize',
    ApplyCustomizationsEnabled: 'applyCustomizationsEnabled',
    UploadFileEnabled: 'uploadFileEnabled',
    FileFromRepoEnabled: 'fileFromRepoEnabled',
    FileCustomizations: 'fileCustomizations',
    RepoCustomizations: 'repoCustomizations',
    SelectedIntrinsicTasks: 'selectedIntrinsicTasks',
};

export interface ScheduleViewModel {
    displayFrequency: string;
    displayTime: string;
}
